import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AdminMenuItem } from '@ytclip/shared'
import { ExternalLink, LayoutList, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.tsx'
import { SwitchField } from '@/components/ui/field.tsx'
import { Badge, EmptyState, ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { TableScroller, Td, Th } from '@/components/ui/misc.tsx'
import { deleteMenuItem, updateMenuItem } from '@/features/admin/api.ts'
import { useAdminMenu } from '@/features/shared-hooks.ts'
import { ApiRequestError } from '@/lib/api-client.ts'
import { MenuItemDialog } from './app-menu-dialog.tsx'

function describe(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback
}

export function MenuPanel() {
  const [editing, setEditing] = useState<AdminMenuItem | null>(null)
  const [creating, setCreating] = useState(false)

  const queryClient = useQueryClient()
  const { data, isPending, isError } = useAdminMenu()

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-menu'] })

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-6">
        <p className="text-body-md text-on-surface-variant">
          Link di sidebar aplikasi. Berlaku saat aplikasi dibuka berikutnya.
        </p>
        <Button onClick={() => setCreating(true)} icon={<Plus className="size-4" />}>
          Tambah
        </Button>
      </div>

      <div className="pt-4">
        {isPending ? (
          <LoadingBlock />
        ) : isError ? (
          <ErrorState message="Menu tidak bisa dimuat." />
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<LayoutList />}
            title="Belum ada menu"
            description="Aplikasi akan memakai menu bawaannya sampai ada yang ditambahkan."
          />
        ) : (
          <TableScroller>
            <table className="w-full min-w-3xl border-collapse">
              <thead>
                <tr className="border-b border-outline-variant">
                  <Th className="w-16 text-right">Urutan</Th>
                  <Th>Label</Th>
                  <Th>Tautan</Th>
                  <Th>Status</Th>
                  <Th><span className="sr-only">Aksi</span></Th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((item) => (
                  <MenuRow
                    key={item.id}
                    item={item}
                    onEdit={() => setEditing(item)}
                    onChanged={invalidate}
                  />
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
      </div>

      <MenuItemDialog
        item={editing}
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        onDone={invalidate}
      />
    </>
  )
}

function MenuRow({
  item,
  onEdit,
  onChanged,
}: {
  item: AdminMenuItem
  onEdit: () => void
  onChanged: () => void
}) {
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => updateMenuItem(item.id, { enabled }),
    onSuccess: onChanged,
    onError: (error) => toast.error(describe(error, 'Gagal mengubah status menu')),
  })

  const remove = useMutation({
    mutationFn: () => deleteMenuItem(item.id),
    onSuccess: () => {
      toast.success('Menu dihapus')
      onChanged()
    },
    onError: (error) => toast.error(describe(error, 'Gagal menghapus menu')),
  })

  return (
    <tr className="border-b border-outline-variant/50 transition-colors hover:bg-surface-container-low">
      <Td className="text-right tabular-nums text-on-surface-variant">{item.position}</Td>
      <Td>
        <span className="block font-medium text-on-surface">{item.label}</span>
        <span className="block font-mono text-label-sm text-on-surface-variant">
          {item.icon}
        </span>
      </Td>
      <Td>
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex max-w-xs items-center gap-1 truncate text-label-md text-primary hover:underline"
        >
          <span className="truncate">{item.url}</span>
          <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
        </a>
      </Td>
      <Td>
        <Badge tone={item.enabled ? 'success' : 'neutral'}>
          {item.enabled ? 'Tampil' : 'Disembunyikan'}
        </Badge>
      </Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-2">
          <SwitchField
            label="Tampilkan"
            checked={item.enabled}
            disabled={toggle.isPending}
            onChange={(enabled) => toggle.mutate(enabled)}
          />
          <Button variant="text" size="sm" onClick={onEdit}>
            Ubah
          </Button>
          <Button
            variant="text"
            size="sm"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
            icon={<Trash2 className="size-4" />}
            className="text-error hover:bg-error/10"
          >
            Hapus
          </Button>
        </div>
      </Td>
    </tr>
  )
}
